import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { schoolAPI, publicAPI, favoriteAPI } from '@/services/api';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import ImageViewer from "@/components/ImageViewer";
import BackLink from "@/components/BackLink";
import LocationViewer from "@/components/LocationViewer";
import StickyInfoCard from "@/components/StickyInfoCard";

// Fix leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
	iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
	iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
	shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

export default function RidingSchoolDetailPage() {
	const { id } = useParams();
	const { t } = useTranslation();
	const { user } = useAuth();
	const navigate = useNavigate();
	const [ad, setAd] = useState(null);
	const [loading, setLoading] = useState(true);
	const [isFav, setIsFav] = useState(false);
	const [pricing, setPricing] = useState({
		free_images_limit: 5, price_extra_image: 0.70,
		free_videos_limit: 1, price_extra_video: 3.00,
		max_video_size_mb: 50, price_featured_ad: 10.00,
		vat_percent: 23
	});

	const API_BASE = process.env.REACT_APP_BACKEND_URL;

	useDocumentMeta({
		title: ad ? `${ad.title} | Equi-Market` : 'Equi-Market',
		description: ad?.description?.slice(0, 200),
		image: ad?.images?.[0] ? (ad.images[0].startsWith('http') ? ad.images[0] : `${API_BASE}${ad.images[0]}`) : '',
		url: ad ? `${window.location.origin}/schools/${ad.slug || ad.id}` : '',
		price: ad?.price,
		currency: 'EUR',
		type: 'school',
	});

	useEffect(() => {
		setLoading(true);
		publicAPI
			.settings()
			.then(r => setPricing(prev => ({ ...prev, ...r.data })))
			.catch(() => {});
		schoolAPI.get(id)
			.then((r) => {
				setAd(r.data);
				if (user) {
					favoriteAPI
						.check(r.data.id)
						.then(f => setIsFav(f.data.is_favorite))
						.catch(() => {});
				}
			})
			.catch(() => navigate('/schools'))
			.finally(() => setLoading(false));
	}, [id, navigate]);

	if (loading || !ad) {
		return <div className="pt-24 min-h-screen flex items-center justify-center text-[#666055]">Loading...</div>;
	}

	if (!ad) return null;

	const disciplines = ad.disciplines || [];
	const levels = ad.levels || [];
	const ageGroups = ad.age_groups || [];
	const facilities = ad.facilities || [];
	const lessonTypes = ad.lesson_types || [];
	const hiddenImages = ad.hidden_images || [];
	const images = (ad.images || []).filter(url => !hiddenImages.includes(url)).map((url, i) => ({
		url,
		preview: url.startsWith('/') ? `${ API_BASE }${ url }` : url,
		thumbnail: ad.thumbnails?.[i] ? (ad.thumbnails[i].startsWith('/') ? `${ API_BASE }${ ad.thumbnails[i] }` : ad.thumbnails[i]) : null
	}));
	const hasLocation = ad.location_lat && ad.location_lng;

	return (
		<div className="pt-20 md:pt-24 min-h-screen" data-testid={`school-detail-${ad.id}`}>
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
				{/* Breadcrumb */}
				<BackLink title={ad.title} uri="/schools" textKey='nav.schools' />

				<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
					{/* Left - Images & Content */}
					<div className="lg:col-span-2 space-y-6" data-testid="horse-gallery">
						{/* Main Image */}
						<ImageViewer images={images} title={ad.title} is_featured={ad.is_featured} />

						{/* Description */}
						<div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
							<h2 className="text-xs font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-3">
								{t('horse.description')}
							</h2>
							<p className="text-sm text-[#3a3a3a] leading-relaxed whitespace-pre-line">
								{ad.description}
							</p>
						</div>

						{/* Details Grid */}
						{ disciplines.length > 0 && (
							<div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
								<h2 className="text-xs font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-3">
									{ t('school.disciplines') }
								</h2>
								<div className="flex flex-wrap gap-2">
									{ disciplines.map(d => (
										<span key={ d }
													className="text-xs bg-[#F2F0E9] text-[#1A3C34] px-3 py-1.5 rounded-sm border border-[#E6E2D6]"
													data-testid={ `school-discipline-${ d }` }>
													{ t(`school.discipline_opt.${ d }`, d) }
												</span>
									)) }
								</div>
							</div>
						) }

						{ (levels.length > 0 || ageGroups.length > 0 || lessonTypes.length > 0) && (
							<div className="bg-white border border-[#E6E2D6] rounded-sm p-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
								{ levels.length > 0 && (
									<div>
										<h3 className="text-xs font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-2">
											{ t('school.levels') }
										</h3>
										<ul className="space-y-1 text-sm text-[#3a3a3a]">
											{ levels.map(l => <li key={ l }>· { t(`school.level_opt.${ l }`, l) }</li>) }
										</ul>
									</div>
								) }
								{ ageGroups.length > 0 && (
									<div>
										<h3 className="text-xs font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-2">
											{ t('school.age_groups') }
										</h3>
										<ul className="space-y-1 text-sm text-[#3a3a3a]">
											{ ageGroups.map(a => <li key={ a }>· { t(`school.age_opt.${ a }`, a) }</li>) }
										</ul>
									</div>
								) }
								{ lessonTypes.length > 0 && (
									<div>
										<h3 className="text-xs font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-2">
											{ t('school.lesson_types') }
										</h3>
										<ul className="space-y-1 text-sm text-[#3a3a3a]">
											{ lessonTypes.map(lt => <li key={ lt }>· { t(`school.lesson_type_opt.${ lt }`, lt) }</li>) }
										</ul>
									</div>
								) }
							</div>
						) }

						{ facilities.length > 0 && (
							<div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
								<h2 className="text-xs font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-3">
									{ t('school.facilities') }
								</h2>
								<div className="flex flex-wrap gap-2">
									{ facilities.map(f => (
										<span key={ f }
													className="text-xs bg-[#F2F0E9] text-[#1A3C34] px-3 py-1.5 rounded-sm border border-[#E6E2D6]">
													{ t(`school.facility_opt.${ f }`, f) }
												</span>
									)) }
								</div>
							</div>
						) }

						{ ad.certifications && (
							<div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
								<h2 className="text-xs font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-3">
									{ t('school.certifications') }
								</h2>
								<p className="text-sm text-[#3a3a3a] whitespace-pre-line">{ ad.certifications }</p>
							</div>
						) }

						{/* Map */ }
						{ hasLocation &&
							<LocationViewer title={ ad.title }
															location_name={ ad.location_name }
															location_lat={ ad.location_lat }
															location_lng={ ad.location_lng }/>
						}
					</div>

					{/* Right - Sticky Info */}
					<StickyInfoCard ad={ad}
													adType="school"
													is_favorite={isFav}
													pricing={pricing} />

				</div>
			</div>
		</div>
	);
}